import React from 'react';
import Page from 'components/UI/Page';
import BusinessUnitUsersTable from './BusinessUnitUsersTable';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { useBusinessUnitUsers } from 'hooks/useBusinessUnitUsers';
import { useAuth } from 'lib/Auth';
import Modal from 'components/UI/Modal';
import { Main } from 'styles/themeColors';
import { USER_ROLE_MAPPER } from 'constants/user';
import { USER_FIELDS, USERS_BUSINESS_UNIT_CONSTANTS } from 'constants/usersBusinessUnit';
import { GENERAL_CONSTANTS } from 'constants/general';
import { useUserDeletion } from 'hooks/useUserDeletion';
import SearchComponent from 'components/UI/SearchComponent';
import styled from 'styled-components';
import { isVendorOnlyByBusinessUnit, getBusinessUnitById } from 'utils/role';
import { BusinessUnit } from 'data/BusinessUnit';

const FilterRow = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: stretch;
    margin-bottom: 16px;
    gap: 16px;
    
    > div {
        width: 25%;
    }
`;

interface Props {
    businessUnitId?: string;
    selectedVendor?: any;
    setUserProfileId: (id: number) => void;
    onInputChange: (field: string, value: any) => void;
    toggleInviteModal: () => void;
    reload: boolean;
    setReload: (reload: boolean) => void;
    selectedBusinessUnit?: BusinessUnit;
}

const UsersBusinessUnit: React.FC<Props> = ({ businessUnitId, setUserProfileId, onInputChange, toggleInviteModal, reload, setReload, selectedBusinessUnit }) => {
    const { user } = useAuth();
    
    // Get current business unit and determine vendor-only status
    const currentBusinessUnit = getBusinessUnitById(selectedBusinessUnit?.id);
    const isVendorOnlyUser = isVendorOnlyByBusinessUnit(user!, currentBusinessUnit);

    // Centralized state management using the useBusinessUnitUsers hook
    const {
        tableData,
        loading,
        currentPage,
        rowsPerPage,
        sortBy,
        sortOrder,
        hasInitialLoad,
        tableKey,
        selectedRows,
        // Actions
        setSelectedRows,
        handlePageChange,
        handleRowsPerPageChange,
        handleSortChange,
        handleRemove,
        handleDeleteMultiple,
        searchTerm,
        setSearchTerm
    } = useBusinessUnitUsers({
        businessUnitId,
        reload,
        setReload
    });

    // User deletion logic using the custom hook
    const {
        showDeleteModal,
        handleRemoveUser,
        handleBulkRemoveUser,
        handleProfileDeletion,
        onCloseDeleteModal
    } = useUserDeletion({ handleRemove, handleDeleteMultiple });

    // Custom handlers that can be extended with business logic
    const handleEditUser = (userProfileId: number) => {
        toggleInviteModal();
        setUserProfileId(userProfileId);
        if (tableData?.data?.length > 0) {
            const user = tableData.data.find((u) => u.id === userProfileId);
            if (user) {
                onInputChange(USER_FIELDS.NAME as any, user.name || '');
                onInputChange(USER_FIELDS.EMAIL as any, user.email || '');
                onInputChange(USER_FIELDS.USER_ROLE as any, { id: user.role, value: USER_ROLE_MAPPER[user.role] });
                onInputChange(USER_FIELDS.GIN_ID as any, user.ginId || '');
            }
        }
    }



    // Show loading state during initial load
    if (loading && !hasInitialLoad) {
        return (
            <Page>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingIndicator />
                </div>
            </Page>
        );
    }
    return (
        <Page>
            <FilterRow>
                <div>
                    <SearchComponent
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        placeHolder={GENERAL_CONSTANTS.SEARCH_EMAIL_USERNAME} 
                        padding={'0'} 
                        useGrid={false}
                    />
                </div>
            </FilterRow>

            {/* Pass all state and handlers to the table component */}
            <BusinessUnitUsersTable
                // Data props
                data={tableData.data}
                totalCount={tableData.totalCount}
                loading={loading}
                tableKey={tableKey}

                // Pagination props
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}

                // Sorting props
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}

                // Selection props
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}

                // Action handlers
                onEdit={handleEditUser}
                onRemove={handleRemoveUser}
                onRemoveBulkUser={() => handleBulkRemoveUser(selectedRows)}

                // UI state
                isVendorOnly={isVendorOnlyUser}
                hasInitialLoad={hasInitialLoad}
            />

            <Modal
                showModal={showDeleteModal}
                onClose={onCloseDeleteModal}
                title={GENERAL_CONSTANTS.CONFIRM_DELETE}
                primaryButtonText={GENERAL_CONSTANTS.DELETE}
                ghostButtonText={GENERAL_CONSTANTS.CANCEL}
                primaryButtonColor={Main.brandDark}
                color={Main.black}
                borderColor={Main.disabled}
                onClick={handleProfileDeletion}
            >
                {USERS_BUSINESS_UNIT_CONSTANTS.DELETE_USER_PROFILE}
            </Modal>
        </Page>
    );
};

export default UsersBusinessUnit;